import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiGet, apiPut, apiPost, apiDelete } from '../services/api';
import {
  ArrowLeft,
  Save,
  Shield,
  FolderKanban,
  Plus,
  X,
  AlertCircle,
} from 'lucide-react';

interface UserData {
  id: string;
  username: string;
  email: string;
  full_name: string | null;
  is_active: boolean;
  role: string;
  last_login: string | null;
  project_count: number;
  created_at: string;
}

interface ProjectAssignment {
  id: string;
  project_id: string;
  project_name: string;
  project_code: string | null;
  project_status: string | null;
  assigned_at: string | null;
}

interface Project {
  id: string;
  name: string;
  code: string | null;
}

export default function UserDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [user, setUser] = useState<UserData | null>(null);
  const [assignments, setAssignments] = useState<ProjectAssignment[]>([]);
  const [allProjects, setAllProjects] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [showAddProject, setShowAddProject] = useState(false);

  // Editable fields
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [role, setRole] = useState('user');
  const [isActive, setIsActive] = useState(true);

  useEffect(() => {
    if (id) {
      fetchUser();
      fetchAssignments();
      fetchProjects();
    }
  }, [id]);

  const fetchUser = async () => {
    setIsLoading(true);
    try {
      const data = await apiGet<UserData>(`/api/users/${id}`);
      setUser(data);
      setEmail(data.email);
      setFullName(data.full_name || '');
      setRole(data.role);
      setIsActive(data.is_active);
    } catch (err) {
      console.error('Failed to fetch user:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAssignments = async () => {
    try {
      const data = await apiGet<ProjectAssignment[]>(`/api/users/${id}/projects`);
      setAssignments(data);
    } catch (err) {
      console.error('Failed to fetch assignments:', err);
    }
  };

  const fetchProjects = async () => {
    try {
      const data = await apiGet<{ items: Project[] }>('/api/projects?page_size=100');
      setAllProjects(data.items);
    } catch {
      // ignore
    }
  };

  const handleSave = async () => {
    setError('');
    setSuccessMsg('');
    setIsSaving(true);
    try {
      await apiPut(`/api/users/${id}`, {
        email,
        full_name: fullName || undefined,
        role,
        is_active: isActive,
      });
      setSuccessMsg('User updated successfully');
      fetchUser();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update user');
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddProject = async (projectId: string) => {
    try {
      await apiPost(`/api/users/${id}/projects/${projectId}`);
      fetchAssignments();
      setShowAddProject(false);
    } catch (err) {
      console.error('Failed to add project:', err);
    }
  };

  const handleRemoveProject = async (projectId: string) => {
    try {
      await apiDelete(`/api/users/${id}/projects/${projectId}`);
      fetchAssignments();
    } catch (err) {
      console.error('Failed to remove project:', err);
    }
  };

  const assignedProjectIds = new Set(assignments.map((a) => a.project_id));
  const availableProjects = allProjects.filter((p) => !assignedProjectIds.has(p.id));

  if (isLoading) {
    return (
      <div className="p-8 text-center text-gray-500">Loading...</div>
    );
  }

  if (!user) {
    return (
      <div className="p-8 text-center text-gray-500">User not found</div>
    );
  }

  return (
    <div className="space-y-6 max-w-3xl">
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/admin/users')}
          className="p-2 hover:bg-dark-800 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5 text-gray-400" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-white">
            {user.full_name || user.username}
          </h1>
          <p className="text-gray-400">@{user.username}</p>
        </div>
      </div>

      {error && (
        <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-2 text-red-400">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <span className="text-sm">{error}</span>
        </div>
      )}

      {successMsg && (
        <div className="p-3 bg-green-500/10 border border-green-500/20 rounded-lg text-green-400 text-sm">
          {successMsg}
        </div>
      )}

      {/* User Info */}
      <div className="bg-dark-900 rounded-xl border border-dark-800 p-6 space-y-4">
        <h2 className="text-lg font-semibold text-white flex items-center gap-2">
          <Shield className="w-5 h-5 text-primary-500" />
          User Details
        </h2>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-2 bg-dark-800 border border-dark-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Full Name</label>
            <input
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              className="w-full px-4 py-2 bg-dark-800 border border-dark-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Role</label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className="w-full px-4 py-2 bg-dark-800 border border-dark-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="user">User</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Status</label>
            <select
              value={isActive ? 'active' : 'inactive'}
              onChange={(e) => setIsActive(e.target.value === 'active')}
              className="w-full px-4 py-2 bg-dark-800 border border-dark-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>

        <div className="text-sm text-gray-500 space-y-1">
          <p>Member since: {new Date(user.created_at).toLocaleDateString()}</p>
          {user.last_login && (
            <p>Last login: {new Date(user.last_login).toLocaleString()}</p>
          )}
        </div>

        <div className="flex justify-end">
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="flex items-center gap-2 px-4 py-2 bg-primary-500 hover:bg-primary-600 disabled:bg-primary-500/50 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
          >
            <Save className="w-4 h-4" />
            {isSaving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      {/* Project Assignments */}
      <div className="bg-dark-900 rounded-xl border border-dark-800 p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <FolderKanban className="w-5 h-5 text-primary-500" />
            Project Assignments
          </h2>
          <button
            onClick={() => setShowAddProject(!showAddProject)}
            className="flex items-center gap-1 px-3 py-1.5 text-sm bg-dark-800 hover:bg-dark-700 text-gray-300 hover:text-white rounded-lg transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add Project
          </button>
        </div>

        {showAddProject && availableProjects.length > 0 && (
          <div className="bg-dark-800 rounded-lg p-3 space-y-2 max-h-48 overflow-y-auto">
            {availableProjects.map((project) => (
              <button
                key={project.id}
                onClick={() => handleAddProject(project.id)}
                className="w-full text-left px-3 py-2 text-sm text-gray-300 hover:text-white hover:bg-dark-700 rounded-lg transition-colors"
              >
                {project.name}
                {project.code && (
                  <span className="text-gray-500 ml-1">({project.code})</span>
                )}
              </button>
            ))}
          </div>
        )}

        {showAddProject && availableProjects.length === 0 && (
          <p className="text-sm text-gray-500">All projects are already assigned.</p>
        )}

        <div className="space-y-2">
          {assignments.length === 0 ? (
            <p className="text-sm text-gray-500 py-4 text-center">
              No projects assigned yet.
              {role === 'admin' && ' Admins have access to all projects by default.'}
            </p>
          ) : (
            assignments.map((assignment) => (
              <div
                key={assignment.id}
                className="flex items-center justify-between py-2 px-3 bg-dark-800 rounded-lg"
              >
                <div>
                  <span className="text-white text-sm">{assignment.project_name}</span>
                  {assignment.project_code && (
                    <span className="text-gray-500 text-sm ml-2">
                      ({assignment.project_code})
                    </span>
                  )}
                  {assignment.project_status && (
                    <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${
                      assignment.project_status === 'active'
                        ? 'bg-green-500/20 text-green-400'
                        : 'bg-gray-500/20 text-gray-400'
                    }`}>
                      {assignment.project_status}
                    </span>
                  )}
                </div>
                <button
                  onClick={() => handleRemoveProject(assignment.project_id)}
                  className="p-1 text-gray-500 hover:text-red-400 transition-colors"
                  title="Remove assignment"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
